import React, { useState, useEffect, useCallback } from "react";
import { Drawer, Select, Button, Spin, Tag, message, Popconfirm } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { getTopicJourneys, addJourneyMapping, removeJourneyMapping } from "../../../services/polly/classificationService";

const { Option } = Select;

const COVERAGE_COLORS = {
  primary: 'green',
  supporting: 'orange',
};

export default function ConnectedJourneysDrawer({ visible, topic, onClose, onUpdate }) {
  const [data, setData] = useState({ mappings: [], availableJourneys: [] });
  const [loading, setLoading] = useState(false);
  const [selectedJourneyId, setSelectedJourneyId] = useState(null);
  const [selectedCoverage, setSelectedCoverage] = useState('supporting');
  const [adding, setAdding] = useState(false);

  const topicId = topic?.id;

  const fetchData = useCallback(async () => {
    if (!topicId) return;
    setLoading(true);
    try {
      const res = await getTopicJourneys(topicId);
      setData(res);
    } catch (err) {
      message.error("Failed to load journey mappings");
    }
    setLoading(false);
  }, [topicId]);

  useEffect(() => {
    if (visible && topicId) {
      fetchData();
      setSelectedJourneyId(null);
    }
  }, [visible, topicId]); // eslint-disable-line react-hooks/exhaustive-deps

  const mappedJourneyIds = data.mappings.map((m) => m.journeyId);
  const unmappedJourneys = data.availableJourneys.filter((j) => !mappedJourneyIds.includes(j.journeyId));

  const handleAdd = async () => {
    if (!selectedJourneyId) return;
    setAdding(true);
    try {
      await addJourneyMapping(topic.id, selectedJourneyId, selectedCoverage);
      message.success("Journey connected");
      setSelectedJourneyId(null);
      setSelectedCoverage('supporting');
      await fetchData();
    } catch (err) {
      message.error("Failed to add mapping");
    }
    setAdding(false);
  };

  const handleRemove = async (mappingId) => {
    try {
      await removeJourneyMapping(topic.id, mappingId);
      message.success("Journey disconnected");
      await fetchData();
    } catch (err) {
      message.error("Failed to remove mapping");
    }
  };

  return (
    <Drawer
      title={`Connected Journeys — ${topic?.name || ''}`}
      placement="right"
      width={460}
      open={visible}
      onClose={() => { onClose(); onUpdate && onUpdate(); }}
    >
      <Spin spinning={loading}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
            Journeys that proactively address this topic. This helps measure journey coverage vs. knowledge gaps.
          </div>

          {data.mappings.length === 0 && !loading && (
            <div style={{ color: '#999', fontSize: 13, padding: '12px 0' }}>
              No journeys connected to this topic yet.
            </div>
          )}

          {data.mappings.map((m) => (
            <div key={m.mappingId} className="ca-journey-row">
              <div className="ca-journey-name">{m.journeyName}</div>
              <Tag color={COVERAGE_COLORS[m.coverage]}>{m.coverage}</Tag>
              <Popconfirm
                title="Disconnect this journey?"
                onConfirm={() => handleRemove(m.mappingId)}
                okText="Disconnect"
                cancelText="Cancel"
              >
                <Button type="text" size="small" icon={<DeleteOutlined />} danger />
              </Popconfirm>
            </div>
          ))}
        </div>

        {unmappedJourneys.length > 0 && (
          <div className="ca-journey-add-section">
            <div style={{ fontSize: 11, fontWeight: 500, color: '#888', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>
              Connect a journey
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Select
                placeholder="Select journey..."
                value={selectedJourneyId}
                onChange={setSelectedJourneyId}
                size="small"
                style={{ flex: 1 }}
              >
                {unmappedJourneys.map((j) => (
                  <Option key={j.journeyId} value={j.journeyId}>{j.journeyName}</Option>
                ))}
              </Select>
              <Select
                value={selectedCoverage}
                onChange={setSelectedCoverage}
                size="small"
                style={{ width: 120 }}
              >
                <Option value="primary">Primary</Option>
                <Option value="supporting">Supporting</Option>
              </Select>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                loading={adding}
                disabled={!selectedJourneyId}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </Spin>
    </Drawer>
  );
}
